// Dashboard.js - VERSÃO CORRIGIDA com Authorization headers
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './Dashboard.css';
import {
  Activity, Zap, Clock, ThermometerSun,
  Wind, AlertCircle, RefreshCw
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const Dashboard = ({ apiUrl }) => {
  const { t, i18n } = useTranslation();
  // As datas e horas seguem o idioma escolhido, não a localização do browser
  const loc = () => (i18n.language === 'en' ? 'en-GB' : 'pt-PT');
  const [stats, setStats] = useState(null);
  const [temperatureData, setTemperatureData] = useState([]);
  const [uptimeData, setUptimeData] = useState([]);
  const [energyData, setEnergyData] = useState([]);
  const [recentActions, setRecentActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(24);

  // Helper para obter headers de autenticação
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = getAuthHeaders();

      // Stats gerais
      const statsRes = await fetch(`${apiUrl}/dashboard/stats`, { headers });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Temperatura agregada
      const tempRes = await fetch(`${apiUrl}/dashboard/temperature/aggregated?hours=${timeRange}&interval_minutes=30`, { headers });
      if (tempRes.ok) {
        const tempData = await tempRes.json();
        setTemperatureData(tempData?.data || []);
      }

      // Uptime
      const uptimeRes = await fetch(`${apiUrl}/dashboard/uptime?hours=${timeRange}`, { headers });
      if (uptimeRes.ok) {
        const uptimeDataRes = await uptimeRes.json();
        setUptimeData(uptimeDataRes?.data || []);
      }

      // Energia
      const energyRes = await fetch(`${apiUrl}/dashboard/energy/estimate?hours=${timeRange}&kwh_rate=0.20`, { headers });
      if (energyRes.ok) {
        const energyDataRes = await energyRes.json();
        setEnergyData(energyDataRes?.data || []);
      }

      // Ações recentes
      const actionsRes = await fetch(`${apiUrl}/dashboard/actions/recent?limit=10`, { headers });
      if (actionsRes.ok) {
        const actionsData = await actionsRes.json();
        setRecentActions(actionsData?.data || []);
      }

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, timeRange, getAuthHeaders]);

  useEffect(() => {
    loadAllData();
    const interval = setInterval(loadAllData, 60000); // Atualizar a cada minuto
    return () => clearInterval(interval);
  }, [loadAllData]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  if (loading && !stats) {
    return (
      <div className="dashboard-loading">
        <RefreshCw className="spin" size={48} />
        <p>{t('dashboard.aCarregar')}</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header-controls">
        <div className="time-range-selector">
          <button
            className={timeRange === 24 ? 'active' : ''}
            onClick={() => setTimeRange(24)}
          >
            24h
          </button>
          <button
            className={timeRange === 168 ? 'active' : ''}
            onClick={() => setTimeRange(168)}
          >
            {t('dashboard.periodo7d')}
          </button>
          <button
            className={timeRange === 720 ? 'active' : ''}
            onClick={() => setTimeRange(720)}
          >
            {t('dashboard.periodo30d')}
          </button>
        </div>
        <button onClick={loadAllData} className="refresh-btn">
          <RefreshCw size={16} />
          {t('comum.atualizar')}
        </button>
      </div>

      {error && (
        <div className="dashboard-error">
          <AlertCircle size={24} />
          <span>{t('comum.erro')}: {error}</span>
        </div>
      )}

      {/* Cards de Estatísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon devices">
            <Wind size={32} />
          </div>
          <div className="stat-content">
            <h3>{t('dashboard.dispositivosTotais')}</h3>
            <p className="stat-value">{stats?.total_devices || 0}</p>
            <span className="stat-label">
              {stats?.devices_online || 0} online · {stats?.devices_offline || 0} offline
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon uptime">
            <Activity size={32} />
          </div>
          <div className="stat-content">
            <h3>{t('dashboard.uptime')}</h3>
            <p className="stat-value">{stats?.uptime_percentage?.toFixed(1) || 0}%</p>
            <span className="stat-label">Últimas {timeRange}h</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon temperature">
            <ThermometerSun size={32} />
          </div>
          <div className="stat-content">
            <h3>{t('dashboard.temperaturaMedia')}</h3>
            <p className="stat-value">{stats?.avg_temperature?.toFixed(1) || 0}°C</p>
            <span className="stat-label">{t('dashboard.todosDispositivos')}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon actions">
            <Clock size={32} />
          </div>
          <div className="stat-content">
            <h3>{t('dashboard.accoesHoje')}</h3>
            <p className="stat-value">{stats?.total_actions_today || 0}</p>
            <span className="stat-label">{t('dashboard.comandosExecutados')}</span>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="charts-grid">
        {/* Gráfico de Temperatura */}
        <div className="chart-card">
          <h3>
            <ThermometerSun size={24} />
            {t('dashboard.historicoTemperatura')}
          </h3>
          {temperatureData && temperatureData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={temperatureData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={(time) => new Date(time).toLocaleTimeString(loc(), {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                />
                <YAxis domain={[18, 30]} />
                <Tooltip
                  labelFormatter={(time) => new Date(time).toLocaleString(loc())}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="temperature"
                  stroke="#ff6b6b"
                  name={t('dashboard.legendaTemperatura')}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data">
              <Activity size={48} />
              <p>{t('dashboard.semTemperatura', { horas: timeRange })}</p>
              <span>{t('dashboard.dadosEmBreve')}</span>
            </div>
          )}
        </div>

        {/* Gráfico de Uptime */}
        <div className="chart-card">
          <h3>
            <Activity size={24} />
            {t('dashboard.uptimePorDispositivo')}
          </h3>
          {uptimeData && uptimeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={uptimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="device_name" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="uptime_percentage" fill="#4ecdc4" name={t('dashboard.legendaUptime')} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data">
              <Activity size={48} />
              <p>{t('dashboard.semUptime', { horas: timeRange })}</p>
              <span>{t('dashboard.dadosEmBreve')}</span>
            </div>
          )}
        </div>

        {/* Gráfico de Consumo Energético */}
        <div className="chart-card">
          <h3>
            <Zap size={24} />
            {t('dashboard.consumoEstimado')}
          </h3>
          {energyData && energyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={energyData}
                  dataKey="estimated_kwh"
                  nameKey="device_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.device_name}: ${entry.estimated_kwh}kWh`}
                >
                  {energyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data">
              <Zap size={48} />
              <p>{t('dashboard.semConsumo', { horas: timeRange })}</p>
              <span>{t('dashboard.dadosEmBreve')}</span>
            </div>
          )}
        </div>

        {/* Tabela de Ações Recentes */}
        <div className="chart-card">
          <h3>
            <Clock size={24} />
            {t('dashboard.accoesRecentes')}
          </h3>
          {recentActions && recentActions.length > 0 ? (
            <div className="recent-actions-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('dashboard.colDataHora')}</th>
                    <th>{t('dashboard.colDispositivo')}</th>
                    <th>{t('dashboard.colAccao')}</th>
                    <th>{t('dashboard.colUtilizador')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActions.map((action, index) => (
                    <tr key={index}>
                      <td>{new Date(action.timestamp).toLocaleString(loc())}</td>
                      <td>{action.device_name || t('dashboard.desconhecido')}</td>
                      <td>{action.action}</td>
                      <td>{action.user || t('dashboard.sistema')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <Clock size={48} />
              <p>{t('dashboard.semAccoes')}</p>
              <span>{t('dashboard.semAccoesDica')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
